import { SoftwareTalent, WorkExperience } from '../types';

/**
 * Compute years between start and end (or current year if isCurrent).
 * Returns null if dates are insufficient.
 */
function computeYearsAtRole(we: WorkExperience): number | null {
  const start = we.startYear;
  const end = we.isCurrent ? new Date().getFullYear() : we.endYear;
  if (start == null || end == null) return null;
  const years = end - start;
  return years >= 0 ? years : null;
}

/**
 * Filter out companies where the person only held contractor roles.
 * Keeps a company if no work-experience entries exist for it, or if
 * at least one role does NOT contain "contract" in the title.
 */
export function filterContractOnlyCompanies(
  companies: string[],
  workExperience: Array<{ company?: string | null; position?: string | null }>,
): string[] {
  return companies.filter((company) => {
    const roles = workExperience.filter(
      (w) => w.company?.trim().toLowerCase() === company.toLowerCase(),
    );
    return roles.length === 0 || roles.some((w) => !/contract/i.test(w.position ?? ''));
  });
}

/**
 * Build credibility line: Top Company • Top Title • YOE (in that role/company)
 * All three MUST come from the SAME work experience entry (same position).
 * Format: "Meta • Dir Eng • 5 yrs" (compact, scannable)
 *
 * Logic:
 * 1. Prefer pedigree notableCompanies[0] (top company) → find matching work exp → use that entry for company, title, yoe
 * 2. Fallback: workExperience[0] (most recent) → company, title, yoe all from same entry
 * Never mix sources (e.g. pedigree company + overall YOE).
 */
export function buildCredibilityLine(talent: SoftwareTalent): string | null {
  const workExp = talent.workExperience ?? [];
  if (workExp.length === 0) return null;

  const topCompanyFromPedigree = talent.pedigreeSignals?.notableCompanies?.[0]?.trim();
  let match: WorkExperience | undefined;

  if (topCompanyFromPedigree) {
    match = workExp.find(
      (we) => we.company?.trim().toLowerCase() === topCompanyFromPedigree.toLowerCase()
    );
  }
  if (!match) {
    match = workExp[0]; // Most recent experience - all fields from same position
  }

  const company = match.company?.trim();
  if (!company) return null;

  const rawTitle = match.position?.trim() || null;
  const title = rawTitle === 'Unknown Title' ? null : rawTitle;
  const yoeAtRole = computeYearsAtRole(match);
  const parts: string[] = [company];
  if (title) parts.push(title);
  if (yoeAtRole != null) parts.push(`${Math.max(1, yoeAtRole)} yrs`);

  return parts.join(' • ');
}
